import { SignInActions } from '@base/authentication/SignInActions';
import { getUserSettings } from '@base/userSettings';
import { CurrentUserRepresentation, OrganizationInfo, UserSettings } from '@common/api';
import { LoadingScreen } from '@components/loading/LoadingScreen';
import { overseerApi, usersApi } from '@data/rest/api';
import { CurrentUserSettings, userSettingsSelectFunction } from '@data/user/defaultUserSettings';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PropsWithChildren, ReactElement, createContext, useContext, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { URLSearchParams } from 'url';
import { ORGANIZATION_ID } from '../base/authentication/constants';
import { DUMMY_USER } from '../data/user/user';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { explodingObject } from '../util/contextHelpers';
import { handleApiErrorMessage } from '../util/message';

export interface AuthContext {
    user?: CurrentUserRepresentation;
    settings: CurrentUserSettings;
    isSigningIn: boolean;
    organization?: OrganizationInfo;
    tenant?: string;

    setCurrentUserSettings(userSettings: UserSettings): Promise<void>;
    invalidateCurrentUserSettings(): Promise<void>;
    changeTenant(tenant: string): Promise<void>;
}

const enum AuthQueryKey {
    CurrentUser = 'currentUser',
    CurrentUserSettings = 'currentUserSettings',
    Organization = 'organization',
}

const TENANT_QUERY_PARAM_KEY = 't';

const AuthContext = createContext<AuthContext>(explodingObject<AuthContext>('Auth context not set'));

export function AuthContextProvider({ ignore, children }: PropsWithChildren<{ ignore?: boolean }>): ReactElement {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isSigningIn, setIsSigningIn] = useState(true);
    const queryClient = useQueryClient();
    const { data: organization, isInitialLoading: isLoadingOrg } = useQuery(
        [AuthQueryKey.Organization],
        () => overseerApi.getOrganizationInfo(),
        {
            notifyOnChangeProps: ['data', 'isInitialLoading'],
            enabled: !isSigningIn && SignInActions.type === 'keycloak',
            retry: false,
            refetchOnWindowFocus: false,
        }
    );

    const [storedTenant, setStoredTenant] = useLocalStorage<string | null>(ORGANIZATION_ID, null);

    const href = window.location.href;
    const tenantDecoded = useMemo(() => {
        const params = new URLSearchParams(href);
        const tenantEncoded = searchParams.get(TENANT_QUERY_PARAM_KEY) ?? params.get(TENANT_QUERY_PARAM_KEY);
        return tenantEncoded && atob(tenantEncoded);
    }, [href, searchParams]);
    const targetTenant = tenantDecoded ?? storedTenant;
    const defaultTenant = organization?.tenants?.find((t) => t.isDefault) ?? organization?.tenants?.[0];
    // Check if target tenant is available for this organization, if not use the orgs default tenant
    let selectedTenant =
        targetTenant && organization?.tenants?.some((t) => t.name === targetTenant)
            ? targetTenant
            : defaultTenant?.name;

    if (SignInActions.type === 'entrylink' || SignInActions.type === 'local') {
        selectedTenant = SignInActions.getTenant();
    }

    if (SignInActions.type !== 'entrylink' && selectedTenant) {
        SignInActions.setTenant(selectedTenant);
    } else if (SignInActions.type === 'keycloak' && targetTenant && !SignInActions.getTenant()) {
        // Apply stored/URL tenant on first render so the initial organization-info request
        // carries X-TENANT-ID. Without this, a user with a stored non-default-tenant selection
        // would hit the subdomain fallback and get 403 on their first request after reload.
        SignInActions.setTenant(targetTenant);
    }

    const { data: user, isInitialLoading: isLoadingUser } = useQuery<CurrentUserRepresentation>(
        [selectedTenant, AuthQueryKey.CurrentUser],
        async () => (SignInActions.type !== 'entrylink' ? usersApi.getCurrent() : DUMMY_USER),
        {
            staleTime: 5000,
            notifyOnChangeProps: ['data', 'isInitialLoading'],
            enabled: !isSigningIn,
            retry: false,
            refetchOnWindowFocus: false,
        }
    );
    const { data: settings } = useQuery<UserSettings>(
        [selectedTenant, AuthQueryKey.CurrentUserSettings],
        async () => {
            if (SignInActions.type !== 'entrylink') {
                return usersApi.getSmartSettings();
            }

            const settings = window.localStorage.getItem('settings');

            if (settings) {
                return JSON.parse(settings);
            }

            return userSettingsSelectFunction();
        },
        {
            staleTime: Infinity,
            notifyOnChangeProps: ['data'],
            enabled: !isSigningIn,
        }
    );

    // When logging in with only organization id in url populate the search params with tenant id
    // Also, if user has a search param tenant selected that is not available to him replace it with current tenant
    if (SignInActions.type !== 'entrylink' && selectedTenant && (!tenantDecoded || tenantDecoded !== selectedTenant)) {
        // Preserve existing params (e.g. deep-link state) — only (re)set the tenant encoding
        const nextParams = new globalThis.URLSearchParams(searchParams);
        nextParams.set(TENANT_QUERY_PARAM_KEY, btoa(selectedTenant));
        setSearchParams(nextParams);
    }

    // If user has a default tenant selected that is not available to him replace it with current tenant
    if (
        SignInActions.type !== 'entrylink' &&
        selectedTenant &&
        storedTenant &&
        !organization?.tenants?.some((t) => t.name === storedTenant)
    ) {
        setStoredTenant(selectedTenant);
    }

    useEffect(() => {
        let isMounted = true;

        async function signIn() {
            try {
                await SignInActions.signIn();
                const current = SignInActions.type === 'entrylink' ? undefined : await usersApi.getCurrent();
                const currentUserSettings = await getUserSettings(SignInActions.type === 'entrylink');
                queryClient.setQueryData([selectedTenant, AuthQueryKey.CurrentUserSettings], currentUserSettings);
                if (current) {
                    // @ts-expect-error 3rd party library
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    zE?.('messenger', 'loginUser', function (callback: any) {
                        callback(current.zendeskJwt);
                    });
                }
            } catch (error) {
                handleApiErrorMessage(error);
            } finally {
                if (isMounted) {
                    setIsSigningIn(false);
                }
            }
        }

        if (!ignore && !user) {
            signIn();
        }

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ignore, queryClient]);

    const tenant = !selectedTenant && import.meta.env.VITE_MODE === 'development' ? 'farseer-dev' : selectedTenant;
    const context = useMemo<AuthContext>(
        () => ({
            isSigningIn,
            user,
            settings: userSettingsSelectFunction(settings),
            organization,
            tenant,

            async setCurrentUserSettings(userSettings: UserSettings): Promise<void> {
                await queryClient.cancelQueries([selectedTenant, AuthQueryKey.CurrentUserSettings]);
                queryClient.setQueryData([selectedTenant, AuthQueryKey.CurrentUserSettings], userSettings);
            },

            async invalidateCurrentUserSettings(): Promise<void> {
                await queryClient.invalidateQueries([selectedTenant, AuthQueryKey.CurrentUserSettings]);
            },

            async changeTenant(newTenant: string) {
                if (newTenant === tenant) {
                    return;
                }

                const loadingId = toast.loading('Switching tenant');
                try {
                    setStoredTenant(newTenant);
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.set(TENANT_QUERY_PARAM_KEY, btoa(newTenant));
                    navigate({ pathname: '/', search: `?${newSearchParams.toString()}` });
                    queryClient.clear();
                    await queryClient.invalidateQueries([AuthQueryKey.CurrentUser]);
                    await queryClient.invalidateQueries([AuthQueryKey.CurrentUserSettings]);
                    queryClient.setQueryData([AuthQueryKey.Organization], organization);
                    toast.success('Tenant switched successfuly');
                    window.location.reload();
                } catch {
                    toast.error('Failed to fetch new tenant data. Please refresh the page');
                } finally {
                    toast.dismiss(loadingId);
                }
            },
        }),
        [
            isSigningIn,
            user,
            settings,
            organization,
            tenant,
            navigate,
            queryClient,
            searchParams,
            selectedTenant,
            setStoredTenant,
        ]
    );

    const loading = isSigningIn || isLoadingOrg || isLoadingUser;
    return (
        <AuthContext.Provider value={context}>
            {loading && !ignore ? <LoadingScreen message="Signing in..." /> : children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
